import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchForm } from '@/features/search/SearchForm';
import { SearchFilters } from '@/features/search/SearchFilters';
import { useVehicleSearch } from '@/features/search/queries';
import {
  PAGE_SIZE,
  countActiveFilters,
  criteriaToSearchParams,
  parseSearchParams,
  searchCriteriaSchema,
  type SearchCriteria,
} from '@/features/search/criteria';
import { VehicleCard, VehicleCardSkeleton } from '@/features/vehicles/VehicleCard';
import { EmptyState, ErrorState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { isSupabaseConfigured } from '@/lib/supabase/client';
import { Alert } from '@/components/ui/Alert';

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const criteria = parseSearchParams(params);
  const search = useVehicleSearch(criteria);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const update = useCallback(
    (patch: Partial<SearchCriteria>) => {
      const next = { ...criteria, ...patch };
      if (!('page' in patch)) next.page = 1;
      setParams(criteriaToSearchParams(next), { replace: true });
    },
    [criteria, setParams],
  );

  const reset = useCallback(() => {
    setParams(
      criteriaToSearchParams({
        ...searchCriteriaSchema.parse({}),
        lieu: criteria.lieu,
        depart: criteria.depart,
        heureDepart: criteria.heureDepart,
        retour: criteria.retour,
        heureRetour: criteria.heureRetour,
      }),
      { replace: true },
    );
  }, [criteria, setParams]);

  const activeFilters = countActiveFilters(criteria);
  const result = search.data;

  const filtersPanel = (
    <SearchFilters criteria={criteria} onChange={update} onReset={reset} />
  );

  return (
    <div className="-mt-px bg-ink-50 py-[clamp(24px,4vw,48px)]">
      <div className="cf-container">
      <div className="rounded-2xl border border-ink-200 bg-white p-4">
        <SearchForm compact initial={criteria} />
      </div>

      {!isSupabaseConfigured && (
        <Alert tone="warning" title="Supabase non configuré" className="mt-6">
          Le catalogue est lu depuis la base de données. Renseignez vos variables d’environnement
          pour voir les véhicules.
        </Alert>
      )}

      <div className="mt-6 flex flex-col gap-8 lg:flex-row">
        <aside className="hidden w-72 shrink-0 lg:block">
          <div className="sticky top-[88px] rounded-2xl border border-ink-200 bg-white p-5">
            <p className="mb-4 text-sm font-bold text-ink-950">Filtres</p>
            {filtersPanel}
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-[15px] text-ink-700">
                {result ? `${result.total} véhicule${result.total > 1 ? 's' : ''}` : 'Recherche'}
              </h1>
              {result && result.days > 1 && (
                <p className="text-sm text-ink-600">
                  Prix affichés pour {result.days} jours de location.
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                className="lg:hidden"
                onClick={() => setFiltersOpen(true)}
              >
                Filtres{activeFilters > 0 ? ` (${activeFilters})` : ''}
              </Button>
              <Select
                aria-label="Trier les résultats"
                className="h-9 w-auto text-sm"
                value={criteria.tri}
                onChange={(e) => update({ tri: e.target.value as SearchCriteria['tri'] })}
              >
                <option value="price_asc">Prix croissant</option>
                <option value="price_desc">Prix décroissant</option>
                <option value="rating_desc">Mieux notés</option>
                <option value="popular">Plus populaires</option>
              </Select>
            </div>
          </div>

          <div className="mt-5">
            {search.isError && <ErrorState error={search.error} onRetry={() => search.refetch()} />}

            {search.isLoading && (
              <div className="grid gap-6 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]">
                {Array.from({ length: 6 }).map((_, index) => (
                  <VehicleCardSkeleton key={index} />
                ))}
              </div>
            )}

            {result && result.items.length === 0 && (
              <EmptyState
                title="Aucun véhicule ne correspond"
                description="Élargissez vos critères, changez de dates, ou retirez quelques filtres."
                action={
                  <Button variant="secondary" onClick={reset}>
                    Réinitialiser les filtres
                  </Button>
                }
              />
            )}

            {result && result.items.length > 0 && (
              <>
                <div className="grid gap-6 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]">
                  {result.items.map((vehicle) => (
                    <VehicleCard key={vehicle.id} vehicle={vehicle} search={params.toString()} />
                  ))}
                </div>
                <Pagination
                  total={result.total}
                  limit={PAGE_SIZE}
                  offset={result.offset}
                  onChange={(offset) => update({ page: Math.floor(offset / PAGE_SIZE) + 1 })}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <Modal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filtres"
        footer={
          <Button onClick={() => setFiltersOpen(false)} className="w-full">
            Voir les résultats
          </Button>
        }
      >
        {filtersPanel}
      </Modal>
      </div>
    </div>
  );
}
